/**
 * 首页内容配置
 *
 * 将硬编码的内容分离为配置数据，便于国际化和动态配置
 */

export interface FeatureItem {
  id: number;
  title: string;
  description: string;
}

export interface ApplicationItem {
  icon: string;
  name: string;
}

export interface VersionFeature {
  text: string;
}

export interface VersionInfo {
  label: string;
  badgeClass: string;
  badgeText: string;
  badgeTextColor: string;
  features: VersionFeature[];
  recommended?: boolean;
  containerClass: string;
}

// ==================== Hero 区域 ====================
export const hero = {
  title: {
    prefix: '星曜智创',
    highlight: ' 用智能重塑',
    suffix: '创作范式'
  },
  subtitle: '让每一位创作者体验沉浸、高效的创作流程',
  description: '',
  cta: {
    primary: '开始创作',
    secondary: '了解更多'
  },
  tags: ['星曜智能创作', '永久记忆', '零提示词门槛', '多模型支持']
};

// ==================== 创作智能体展示 ====================
export const agentShowcase = {
  badge: '核心能力',
  title: '强大的创作智能体',
  subtitle: '专业智能体协作，复杂任务智能分解，独立上下文避免干扰，拥有全能专家团队',
  features: [
    {
      title: 'AI 智能体',
      description: '行业顶尖大模型，高效智能体执行，理解创作规律。提供全方位创作支持，从灵感激发到内容润色。'
    },
    {
      title: '自动化工作流',
      description: '以智能体为中心的工作流，简单节点，组建高效工作流，让创作过程更加顺畅高效。'
    },
    {
      title: '智能创作沙龙',
      description: '多智能体协同创作，专家调研、大师执笔，一键启动创作团队，实现创作效率飞跃。'
    }
  ]
};

// ==================== 全流程内容创作 ====================
export const contentCreation = {
  badge: '内容创作',
  title: '全流程内容创作',
  subtitle: '专注于文学创作的AI工具，覆盖多类型文学创作需求',
  items: [
    {
      title: '小说',
      description: '专业文学创作AI，支持小说、剧本、文案等多种文体。智能情节推进，人物塑造，场景描写，让创作事半功倍。',
      example: '创作一篇关于古代宫廷爱情的小说开头，要求情节曲折，人物性格鲜明'
    },
    {
      title: '剧本',
      description: '根据创作需求生成剧本格式，包含场景描述、对话、动作指示。支持多种剧本结构，适配不同类型影视创作。',
      example: '写一个三幕剧结构的悬疑短剧剧本，要求紧凑有张力，适合拍摄成短视频'
    },
    {
      title: '文案',
      description: '根据不同的营销需求生成多种风格的文案，适配不同场景和受众。支持产品推广、活动宣传、社交媒体等多种文案类型。',
      example: '为一款新上市的茶叶品牌写10条朋友圈文案，要求清新自然，突出产品特点'
    }
  ]
};

// ==================== 持久化记忆系统 ====================
export const memorySystem = {
  badge: '记忆系统',
  title: '持久化记忆系统',
  subtitle: '跨平台记忆同步，创作灵感永不丢失',
  memoryTitle: '永久记忆',
  memorySubtitle: 'Persistent Memory',
  memoryDescription: 'AI会记住你的创作风格、偏好设置、历史对话，越用越懂你。支持跨会话、跨设备记忆同步，随时随地继续创作。',
  features: [
    '跨会话记忆保持，创作不中断',
    '智能记忆压缩，保留关键信息',
    '支持向量检索，快速回忆过往',
    '隐私保护，记忆数据安全存储'
  ],
  novelclawTitle: 'NovelClaw',
  novelclawSubtitle: '小说龙虾 · 多平台创作助手',
  novelclawDescription: '解锁创作自由，支持微信、钉钉、飞书等多平台接入。无论在哪里，都能随时继续你的创作。',
  platforms: ['微信', '钉钉', '飞书']
};

// ==================== 付费计划 ====================
export const pricing = {
  badge: '付费计划',
  title: '选择适合你的创作方案',
  subtitle: '从免费体验到专业创作，满足不同程度的创作需求',
  footer: '所有方案均可随时升级或降级，订阅可随时取消',
  plans: [
    {
      level: 'LV0',
      name: '执笔人',
      price: 'Free',
      features: [
        '工作区 2个',
        '顶级模型创作 5 prompt/5h',
        '图片生成 5张/天',
        '知识库 1MB/工作区'
      ],
      featured: false,
      recommended: false
    },
    {
      level: 'LV1',
      name: '寻道者',
      price: '49.9元',
      features: [
        '工作区 10个',
        '顶级模型创作 30 prompt/5h',
        '图片生成 50张/天',
        '联网搜索 50次/天',
        '知识库上限 20M'
      ],
      featured: false,
      recommended: false
    },
    {
      level: 'LV2',
      name: '创世神',
      price: '129.9元',
      features: [
        '工作区 30个',
        '顶级模型创作 100 prompt/5h',
        '图片生成 100张/天',
        '联网搜索 100次/天',
        '知识库上限 50M',
        '自动化工作流',
        'Agent Teams',
        '视频创作'
      ],
      featured: true,
      recommended: true
    },
    {
      level: 'LV3',
      name: '文道本源',
      price: '299.9元',
      features: [
        '工作区 不设限',
        '顶级模型创作 300 prompt/5h',
        '图片生成 300张/天',
        '联网搜索 300次/天',
        '知识库上限 100M',
        '自动化工作流',
        'Agent Teams',
        '视频创作',
        'novel-claw全平台自由创作',
        '最新功能及时体验'
      ],
      featured: true,
      recommended: false
    }
  ]
};

// ==================== 使用场景 ====================
export const useCases = {
  badge: '使用场景',
  title: 'AI文学能为你做什么？',
  subtitle: '一句话开启创作，让AI成为你的创作伙伴',
  examples: [
    {
      command: 'novel create "创作一部科幻小说，讲述人类与AI共存的未来世界"',
      description: '📚 智能生成世界观设定、人物档案、情节大纲，开启科幻创作之旅'
    },
    {
      command: 'script generate "写一个三幕剧结构的悬疑短剧剧本"',
      description: '🎭 自动生成剧本格式，包含场景描述、对话、动作指示，可直接拍摄'
    },
    {
      command: 'copy write "为一款新上市的茶叶品牌写10条朋友圈文案"',
      description: '✍️ 生成多种风格的营销文案，适配不同场景和受众'
    },
    {
      command: 'analyze "分析《红楼梦》中林黛玉的人物性格特点"',
      description: '📖 深度文学分析，提供多维度解读和参考文献'
    }
  ],
  tags: ['小说创作', '剧本编写', '文案生成', '文学分析', '诗歌创作', '散文写作', '网文连载', '学术写作']
};

// ==================== 页脚 ====================
export const footer = {
  copyright: '© 2026 星曜智创平台. All rights reserved.',
  links: [
    { label: '关于我们', href: '#' },
    { label: '使用条款', href: '/user-agreement.html' },
    { label: '隐私政策', href: '/privacy-policy.html' },
    { label: '联系我们', href: 'mailto:support@starnovel.cn' }
  ]
};

// ==================== 旧版内容（向后兼容）====================
export const homePageContent = {
  header: {
    title: '星曜智创',
    subtitle: '星曜智创平台',
    readButton: '立即阅读',
    createButton: '立即创作'
  },

  comparison: {
    title: 'AI文学创作VS传统文学创作',
    mainText: 'AI无法取代人类的思想,但可以让人类的思想更加熠熠闪光',
    subText: '结合AI的创造力与人类的想象力,开启文学创作新纪元'
  },

  features: {
    title: '核心功能',
    items: [
      {
        id: 1,
        title: '专业写作大模型',
        description: '深度优化的文学创作模型,理解创作规律'
      },
      {
        id: 2,
        title: '智能体协同创作',
        description: '多智能体协作,提供全方位创作支持'
      },
      {
        id: 3,
        title: '自动化工作流',
        description: '定制化流程,提升创作效率'
      }
    ]
  },

  applications: {
    title: '应用对象',
    items: [
      {
        icon: '📚',
        name: '小说'
      },
      {
        icon: '🎭',
        name: '剧本'
      },
      {
        icon: '✍️',
        name: '文案'
      },
      {
        icon: '📖',
        name: '文学作品'
      }
    ]
  },

  versionComparison: {
    title: '版本对比',
    versions: [
      {
        label: '免费版',
        badgeClass: 'bg-gray-100',
        badgeText: 'text-gray-900',
        badgeTextColor: 'text-gray-900',
        containerClass: 'border-gray-200',
        recommended: false,
        features: [
          { text: '可创建两本小说' },
          { text: '使用标准大模型' },
          { text: '版权共享' }
        ]
      },
      {
        label: '订阅版',
        badgeClass: 'bg-spine-500',
        badgeText: 'text-white',
        badgeTextColor: 'text-spine-600',
        containerClass: 'border-spine-300 bg-spine-50',
        recommended: true,
        features: [
          { text: '可创建无限工作区' },
          { text: '可使用专业大模型/多种顶尖大模型' },
          { text: '可设置自动化工作流' },
          { text: '版权独享' }
        ]
      }
    ]
  },

  footer: {
    copyright: '© 2026 星曜智创平台. All rights reserved.',
    links: [
      { label: '关于我们', href: '#' },
      { label: '使用条款', href: '/user-agreement.html' },
      { label: '隐私政策', href: '/privacy-policy.html' },
      { label: '联系我们', href: 'mailto:support@starnovel.cn' }
    ]
  },

  // 新版内容导出
  hero,
  agentShowcase,
  contentCreation,
  memorySystem,
  pricing,
  useCases
} as const;

export default homePageContent;
